import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const safeParse = (str, fallback = []) => {
  try {
    if (!str) return fallback;
    if (typeof str === "object") return str;
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

const LeftPanelInterview = ({ data, onUpdate }) => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [allQuestions, setAllQuestions] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);

  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [programQuestions, setProgramQuestions] = useState([]);
  const [extraQuestions, setExtraQuestions] = useState([]);
  const [assignedQuestions, setAssignedQuestions] = useState([]);

  const [selectedQ, setSelectedQ] = useState(null);

  // Modal state
  const [showQModal, setShowQModal] = useState(false);
  const [editingQ, setEditingQ] = useState(null);

  // Load all programs
  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/interviews/programs`, { headers: authHeader() })
      .then((res) => setAllPrograms(res.data || []))
      .catch((err) => console.error("Failed to load programs", err));
  }, [BASE_URL]);

  // Load all available questions
  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/interviews/questions`, { headers: authHeader() })
      .then((res) => setAllQuestions(res.data || []))
      .catch((err) => console.error("Failed to load questions", err));
  }, [BASE_URL]);

  // Initialize from position data
  useEffect(() => {
    if (data) {
      const parsedPrograms = safeParse(data.p_category_programs, []);
      setSelectedPrograms(
        Array.isArray(parsedPrograms) ? parsedPrograms.map(Number) : []
      );

      const parsedQuestions = safeParse(data.p_interview_questions, []);
      setExtraQuestions(Array.isArray(parsedQuestions) ? parsedQuestions : []);
    }
  }, [data]);

  // Fetch program questions whenever selectedPrograms changes
  useEffect(() => {
    if (!selectedPrograms.length) {
      setProgramQuestions([]);
      return;
    }

    Promise.all(
      selectedPrograms.map((pid) =>
        axios.get(`${BASE_URL}/api/interviews/programs/${pid}/questions`, {
          headers: authHeader(),
        })
      )
    )
      .then((results) => {
        // flatten and merge
        const merged = results
          .map((res) => res.data || [])
          .flat()
          .filter(Boolean);

        // Deduplicate by iq_id
        const dedup = merged.filter(
          (q, idx, arr) => arr.findIndex((x) => x.iq_id === q.iq_id) === idx
        );

        setProgramQuestions(dedup);
      })
      .catch((err) => console.error("Failed to load program questions", err));
  }, [selectedPrograms, BASE_URL]);

  // Combine program + extra questions (update immediately)
  useEffect(() => {
    const programIds = programQuestions.map((q) => q.iq_id);

    // 🟢 Only keep extras that are NOT in any current program
    const validExtras = extraQuestions.filter((id) => !programIds.includes(id));

    // 🟢 If programs were removed, drop those questions from extras too
    if (validExtras.length !== extraQuestions.length) {
      setExtraQuestions(validExtras);
    }

    const mergedIds = [...new Set([...programIds, ...validExtras])];

    const mapped = mergedIds
      .map((id) => allQuestions.find((q) => q.iq_id === id))
      .filter(Boolean);

    setAssignedQuestions(mapped);
  }, [programQuestions, extraQuestions, allQuestions]);

  // Save everything
  const saveAll = async () => {
    try {
      const questionIds = [
        ...new Set([
          ...programQuestions.map((q) => q.iq_id),
          ...extraQuestions,
        ]),
      ];

      await axios.put(
        `${BASE_URL}/api/interviews/positions/${data.p_id}/interview`,
        {
          programIds: selectedPrograms,
          questionIds,
        },
        { headers: authHeader() }
      );

      window.alert("✅ Questions saved successfully!");
      window.location.reload(); // 🔄 force full reload
    } catch (err) {
      console.error("Failed to save interview setup", err);
      window.alert("❌ Failed to save questions.");
    }
  };

  const deleteQuestion = (qid) => {
    if (!window.confirm("Delete this question?")) return;
    setExtraQuestions(extraQuestions.filter((id) => id !== qid));
  };

  const addQuestion = (qid) => {
    if (!extraQuestions.includes(qid)) {
      setExtraQuestions([...extraQuestions, qid]);
    }
  };

  const openEditQ = (q) => {
    setEditingQ(q);
    setShowQModal(true);
  };

  const saveQuestion = async (qObj) => {
    try {
      await axios.put(
        `${BASE_URL}/api/interviews/questions/${editingQ.iq_id}`,
        qObj,
        { headers: authHeader() }
      );
      setShowQModal(false);
      const res = await axios.get(`${BASE_URL}/api/interviews/questions`, {
        headers: authHeader(),
      });
      setAllQuestions(res.data || []);
    } catch (e) {
      alert("Failed to save question.");
    }
  };

  return (
    <div className="section mb-4">
      {/* Section 1: Programs Multi-select */}
      <div className="section-title">Programs</div>
      <Select
        isMulti
        value={allPrograms
          .filter((p) => selectedPrograms.includes(p.ip_id))
          .map((p) => ({ label: p.ip_name, value: p.ip_id }))}
        options={allPrograms.map((p) => ({ label: p.ip_name, value: p.ip_id }))}
        onChange={(opts) => {
          const ids = opts ? opts.map((o) => o.value) : [];
          setSelectedPrograms(ids);

          // clear programQuestions immediately if deselect all
          if (!ids.length) setProgramQuestions([]);
        }}
        placeholder="Select programs..."
        className="mb-3"
      />

      {/* Section 2: Interview Questions */}
      <div className="section-title">Interview Questions</div>
      {assignedQuestions.length > 0 ? (
        <ul className="list-group mb-3">
          {assignedQuestions.map((q) => (
            <li
              key={q.iq_id}
              className="list-group-item d-flex justify-content-between"
            >
              {q.iq_question}
              <div>
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={() => openEditQ(q)}
                >
                  <i className="bi bi-pencil"></i>
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteQuestion(q.iq_id)}
                  disabled={programQuestions.some((pq) => pq.iq_id === q.iq_id)}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>No questions yet.</p>
      )}

      {/* Section 3: Add Extra Question */}
      <div className="section-title">Add Extra Question</div>
      <div className="d-flex gap-2 mt-2 mb-3">
        <div style={{ flex: 1 }}>
          <Select
            value={selectedQ}
            options={allQuestions
              .filter(
                (q) => !assignedQuestions.find((aq) => aq.iq_id === q.iq_id)
              )
              .map((q) => ({ label: q.iq_question, value: q.iq_id }))}
            onChange={(opt) => setSelectedQ(opt)}
            placeholder="Choose an extra question..."
          />
        </div>
        <Button
          variant="success"
          disabled={!selectedQ}
          onClick={() => {
            if (selectedQ) {
              addQuestion(selectedQ.value);
              setSelectedQ(null);
            }
          }}
        >
          Add
        </Button>
      </div>

      {/* Save All */}
      <Button variant="primary" onClick={saveAll}>
        Save All
      </Button>

      {/* Modal for editing a question */}
      <QuestionEditor
        show={showQModal}
        onClose={() => setShowQModal(false)}
        editingQ={editingQ}
        onSave={saveQuestion}
      />
    </div>
  );
};

export default LeftPanelInterview;

// ---- Question Editor ----
function QuestionEditor({ show, onClose, onSave, editingQ }) {
  const [type, setType] = useState(editingQ?.iq_type || "text");
  const [text, setText] = useState(editingQ?.iq_question || "");
  const [options, setOptions] = useState(() => {
    if (!editingQ?.iq_options) return [];
    if (Array.isArray(editingQ.iq_options)) return editingQ.iq_options;
    try {
      return JSON.parse(editingQ.iq_options);
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (editingQ) {
      setType(editingQ.iq_type);
      setText(editingQ.iq_question);
      if (Array.isArray(editingQ.iq_options)) {
        setOptions(editingQ.iq_options);
      } else {
        try {
          setOptions(
            editingQ.iq_options ? JSON.parse(editingQ.iq_options) : []
          );
        } catch {
          setOptions([]);
        }
      }
    } else {
      setType("text");
      setText("");
      setOptions([]);
    }
  }, [editingQ, show]);

  const addOption = () => setOptions([...options, ""]);
  const updateOption = (i, val) => {
    const copy = [...options];
    copy[i] = val;
    setOptions(copy);
  };
  const deleteOption = (i) => setOptions(options.filter((_, idx) => idx !== i));

  const handleSave = () => {
    onSave({
      iq_question: text,
      iq_type: type,
      iq_options: type === "select" || type === "multiselect" ? options : [],
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Interview Question</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="text">Text</option>
            <option value="textarea">Paragraph</option>
            <option value="number">Number</option>
            <option value="select">Multiple Choice (Single Selection)</option>
            <option value="multiselect">
              Multiple Choice (Multiple Selections)
            </option>
            <option value="boolean">Yes / No</option>
            <option value="date">Date</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Question</label>
          <textarea
            className="form-control"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        {(type === "select" || type === "multiselect") && (
          <div className="mb-3">
            <label className="form-label">Answers</label>
            <div className="list-group">
              {options.map((opt, i) => (
                <div
                  key={i}
                  className="list-group-item d-flex align-items-center gap-2"
                >
                  <input
                    type="text"
                    className="form-control"
                    value={opt}
                    onChange={(e) => updateOption(i, e.target.value)}
                  />
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => deleteOption(i)}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
            <button
              className="btn btn-outline-secondary btn-sm mt-2"
              onClick={addOption}
            >
              + Add Answer
            </button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
